// ---------------------------------------------------------------------------
// Newsletter Renderer
// ---------------------------------------------------------------------------
// Foundational primitive — publishes the editor `Newsletter` block as an
// inert <form> that posts to the platform's newsletter endpoint. The
// endpoint can be overridden per-render via the `action` prop; if omitted
// we render a disabled form so the published page never silently leaks
// emails to "#".

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface NewsletterProps {
  heading?: string;
  description?: string;
  buttonLabel?: string;
  placeholder?: string;
  action?: string;
}

const DEFAULT_HEADING = "Stay in the loop";
const DEFAULT_BUTTON_LABEL = "Subscribe";
const DEFAULT_PLACEHOLDER = "Enter your email";

function nonEmpty(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as NewsletterProps;

  const heading = nonEmpty(p.heading, DEFAULT_HEADING);
  const description = typeof p.description === "string" ? p.description.trim() : "";
  const buttonLabel = nonEmpty(p.buttonLabel, DEFAULT_BUTTON_LABEL);
  const placeholder = nonEmpty(p.placeholder, DEFAULT_PLACEHOLDER);

  const rawAction = typeof p.action === "string" ? p.action.trim() : "";
  const hasAction = rawAction.length > 0;
  const action = hasAction ? url(rawAction) : "";
  const disabledAttrs = hasAction ? "" : " disabled aria-disabled=\"true\"";

  const descriptionHtml = description
    ? `\n    <p class="ease-newsletter__description">${text(description)}</p>`
    : "";

  const formAttrs = hasAction
    ? `action="${action}" method="post"`
    : `data-ease-newsletter="unconfigured"`;

  return `<section class="ease-section ease-newsletter">
  <div class="ease-newsletter__inner">
    <h3 class="ease-newsletter__heading">${text(heading)}</h3>${descriptionHtml}
    <form class="ease-newsletter__form" ${formAttrs}>
      <label class="ease-newsletter__field">
        <span class="ease-newsletter__label">${text(placeholder)}</span>
        <input class="ease-newsletter__input" type="email" name="email" placeholder="${attr(placeholder)}" autocomplete="email" required${disabledAttrs}>
      </label>
      <button class="ease-newsletter__submit ease-btn ease-btn-primary" type="submit"${disabledAttrs}>${text(buttonLabel)}</button>
    </form>
  </div>
</section>`;
}

registerRenderer("newsletter", { render });
